import { DiagnosticCard } from '../DiagnosticCard';
import { MonteCarloSimulation } from '../MonteCarloSimulation';
import { RetirementIncomeProjection } from '../RetirementIncomeProjection';
import { HeroDashboard } from '../HeroDashboard';
import { InsightsFeed } from '@/components/insights/InsightsFeed';
import { ActionPlanPanel } from '../ActionPlanPanel';
import { ScoringConfig } from '@/lib/scoring-config';
import { ClientInfo } from '@/types/portfolio';
import type { Recommendation } from '@/types/portfolio';
import { useProfile } from '@/hooks/useProfile';

interface DashboardOverviewProps {
  sampleAccounts?: unknown[];
  observationCards: any[];
  noIssuesCards: any[];
  scoringConfig: ScoringConfig;
  clientInfo: ClientInfo;
  onSelectCategory: (key: string) => void;
  snapshotData: {
    totalValue: number;
    accountCount: number;
    issuesCount: number;
  };
  partnerView: string;
  partner: any;
  cardContracts: any[];
  actionPlan?: Recommendation[];
  planningProgress?: { completed: number; total: number };
  onClientSelect?: (client: { name: string; riskTolerance: ClientInfo['riskTolerance'] }) => void;
}

export function DashboardOverview({
  observationCards,
  noIssuesCards,
  scoringConfig,
  clientInfo,
  onSelectCategory,
  snapshotData,
  cardContracts,
  actionPlan = [],
  planningProgress,
}: DashboardOverviewProps) {
  // Get retirement goal data from profile
  const { profile, currentAge } = useProfile();
  const retirementGoalAmount = profile?.retirement_goal_amount ?? undefined;
  const retirementTargetAge = profile?.retirement_target_age ?? undefined;

  // Filter out planningGaps from observation cards (it's now shown in Action Plan)
  const filteredObservationCards = observationCards.filter((c: any) => c.id !== 'planningGaps');
  const filteredNoIssuesCards = noIssuesCards.filter((c: any) => c.id !== 'planningGaps');

  // Split observation cards: first 4, then Monte Carlo, then rest
  const firstFourCards = filteredObservationCards.slice(0, 4);
  const remainingCards = filteredObservationCards.slice(4);

  // Default values for HeroDashboard if profile data is incomplete
  const defaultGoalAmount = retirementGoalAmount ?? clientInfo.targetAmount ?? 1500000;
  const effectiveCurrentAge = currentAge ?? clientInfo.currentAge ?? 45;
  // Use profile target age, or derive from yearsToGoal, or default to 65
  const defaultRetirementAge = retirementTargetAge ??
    (clientInfo.yearsToGoal ? effectiveCurrentAge + clientInfo.yearsToGoal : 65);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Hero Dashboard - Immediate value display */}
      <HeroDashboard
        portfolioValue={snapshotData.totalValue}
        currentAge={effectiveCurrentAge}
        retirementAge={defaultRetirementAge}
        goalAmount={defaultGoalAmount}
      />

      {/* Action Plan Header */}
      <ActionPlanPanel
        actionPlan={actionPlan}
        onSelectCategory={onSelectCategory}
        planningProgress={planningProgress}
      />

      <div className="space-y-6">
        {/* Observations Section - First 4 cards */}
        {firstFourCards.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-status-warning" />
              Observations
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {firstFourCards.map((c: any) => (
                <DiagnosticCard
                  key={String(c.id)}
                  name={String(c.title)}
                  subtitle={c.subtitle}
                  iconName={c.iconName ?? "Shield"}
                  categoryKey={String(c.id)}
                  result={{
                    status: c.status,
                    score: c.score,
                    keyFinding: c.keyFinding,
                    headlineMetric: c.headlineMetric,
                    details: c.details,
                  }}
                  onViewDetails={() => onSelectCategory(String(c.id))}
                  scoringConfig={scoringConfig}
                  riskTolerance={clientInfo.riskTolerance}
                />
              ))}
            </div>
          </div>
        )}

        {/* Retirement Modeling Section */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Retirement Income Projection - Full featured */}
          <RetirementIncomeProjection
            portfolioValue={snapshotData.totalValue}
            currentAge={currentAge ?? clientInfo.currentAge}
            retirementAge={retirementTargetAge}
          />

          {/* Monte Carlo Simulation */}
          <MonteCarloSimulation
            portfolioValue={snapshotData.totalValue}
            currentAge={currentAge ?? clientInfo.currentAge}
            goalAmount={retirementGoalAmount}
            targetAge={retirementTargetAge}
          />
        </div>

        {/* Remaining Observation Cards */}
        {remainingCards.length > 0 && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {remainingCards.map((c: any) => (
                <DiagnosticCard
                  key={String(c.id)}
                  name={String(c.title)}
                  subtitle={c.subtitle}
                  iconName={c.iconName ?? "Shield"}
                  categoryKey={String(c.id)}
                  result={{
                    status: c.status,
                    score: c.score,
                    keyFinding: c.keyFinding,
                    headlineMetric: c.headlineMetric,
                    details: c.details,
                  }}
                  onViewDetails={() => onSelectCategory(String(c.id))}
                  scoringConfig={scoringConfig}
                  riskTolerance={clientInfo.riskTolerance}
                />
              ))}
            </div>
          </div>
        )}

        {/* No Issues Detected Section - Full width cards */}
        {filteredNoIssuesCards.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-status-good" />
              No Issues Detected
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {filteredNoIssuesCards.map((c: any) => (
                <DiagnosticCard
                  key={String(c.id)}
                  name={String(c.title)}
                  subtitle={c.subtitle}
                  iconName={c.iconName ?? "Shield"}
                  categoryKey={String(c.id)}
                  result={{
                    status: c.status,
                    score: c.score,
                    keyFinding: c.keyFinding,
                    headlineMetric: c.headlineMetric,
                    details: c.details,
                  }}
                  onViewDetails={() => onSelectCategory(String(c.id))}
                  scoringConfig={scoringConfig}
                  riskTolerance={clientInfo.riskTolerance}
                />
              ))}
            </div>
          </div>
        )}

        {/* Insights Feed - Key Takeaways */}
        <InsightsFeed
          cards={cardContracts}
          onViewCard={(cardId) => onSelectCategory(cardId)}
        />
      </div>
    </div>
  );
}
